import useFetch from '@e3dc-react/shell/Hooks/useFetch'
import { useContext, useEffect } from 'react'
import { UserData } from 'src/State/User/UserModel'
import { UserLocationDto } from '../api/dto/user.dto'
import { useUser } from 'src/Hooks/useUser'
import { useTypedSelector } from 'src/State/RootReducer'
import { fetchUserDataById } from '../api/user.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'

import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import axios, { CancelToken } from 'axios'
import { fetchAuthTokens } from '@e3dc-react/shell/Hooks/use-relogin'
import JWT from 'jsonwebtoken'
import { Decoded } from '@e3dc-react/shell/Types'
import { useDispatch } from 'react-redux'
import { setIsUserIdLoading, setUserId } from '@e3dc-react/shell/State/Auth/AuthActions'
import { decodeToken, getLoginSearchParameters } from 'src/Libs/login'
import { usePortalQuery } from 'src/Libs/api'

//#region Main Hook
interface UseInitUserData {
  user: UserData | undefined
  userLoading: boolean
  hasFetchedUser: boolean
  userContactData: UserLocationDto | undefined
}

/**
 * Custom hook to fetch, save to redux and return user and its contact (location) data
 */
const useInitUserData = (): UseInitUserData => {
  const { setUserData, setContactData } = useUser()
  const userId = useTypedSelector(state => state.auth.userId)
  const refresh = useTypedSelector(state => state.apiView.refresh)
  const { user, isLoading, isFetched } = useFetchUserData(userId)
  useInitaliseUserId()

  const [userContactData] = useFetch<UserLocationDto>({
    url: `users/${userId}/location`,
    dependencies: [userId, refresh],
    doNotFetch: userId === undefined,
  })

  useEffect(() => {
    if (user && userId) setUserData({ ...user, ID: userId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userId])

  useEffect(() => {
    if (userContactData) setContactData(userContactData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContactData])

  return { user: user ?? undefined, userLoading: isLoading, hasFetchedUser: isFetched, userContactData }
}

export default useInitUserData

//#region Helper for Fetching
interface FetchUserDataReturnType {
  user: UserData | undefined
  isLoading: boolean
  isFetched: boolean
}

const useFetchUserData = (userId: number | undefined): FetchUserDataReturnType => {
  const authFetch = useAuthFetchWithResponse()

  const queryFnUser = async (): Promise<UserData | null> => {
    const { error, hasErrors, value } = await fetchUserDataById(userId as number, authFetch)

    if (hasErrors) {
      throw error || new Error('An unexpected error occurred when fetching user')
    }

    return value
  }

  const {
    data: user,
    isLoading,
    isFetched,
  } = usePortalQuery<UserData | null>({
    queryKey: ['users', 'init user data', userId],
    queryFn: queryFnUser,
    enabled: !!userId,
  })

  return { user: user ?? undefined, isLoading, isFetched }
}

//#region Helper for Initialising userId
const useInitaliseUserId = (): number | undefined => {
  const { reAuthToken, authToken } = useContext(AuthContext)
  const source = axios.CancelToken.source()
  const dispatch = useDispatch()
  const search = getLoginSearchParameters()

  const { data, isError } = usePortalQuery<{ userId: number } | undefined>({
    queryKey: ['userIdFromToken', reAuthToken, source.token, authToken],
    queryFn: () => getUserIdFromToken(reAuthToken!, source.token),
    enabled: Boolean(reAuthToken),
  })

  useEffect(() => {
    if (!authToken) return

    const decoded = decodeToken(search?.token)

    if (decoded?.userId) {
      dispatch(setUserId(decoded.userId))
      dispatch(setIsUserIdLoading(false))
    } else if (data) {
      dispatch(setUserId(data.userId))
      dispatch(setIsUserIdLoading(false))
    } else if (isError) {
      dispatch(setIsUserIdLoading(false))
    }

    return () => {
      dispatch(setIsUserIdLoading(true))
    }
  }, [data, search?.token, dispatch, reAuthToken, isError, authToken])

  return data?.userId
}

const getUserIdFromToken = async (reAuthToken: string, token: CancelToken): Promise<{ userId: number } | undefined> => {
  try {
    const { data } = await fetchAuthTokens(reAuthToken, token)
    const newAuthToken = data.token
    const newDecoded = JWT.decode(newAuthToken) as Decoded

    return { userId: newDecoded.userId }
  } catch (error) {
    console.error('Error fetching userId from token', error)
    return undefined
  }
}
